import React, { useRef, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { Box, Tooltip } from '@ui/wrapper';

const MAX_CONTENT_WIDTH = 300;
const DEFAULT_CLAMPING_LINES = 1;

type TruncateProps = {
  lines: number;
};

type Props = {
  children: string;
  lines?: number;
  showTipOnHover?: boolean;
  maxWidth?: number;
};

const Trigger = styled(Tooltip.Trigger)`
  display: flex;
  min-width: 0;
  height: inherit;
  align-items: center;
`;

const lineClampStyle = (lines) => css`
  @supports (-webkit-line-clamp: ${lines}) {
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: ${lines};
    -webkit-box-orient: vertical;
  }
`;

const ellipsedTextStyle = (lines) => css`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  ${lineClampStyle(lines)}
`;

const Truncate = styled.span`
  ${({ lines }: TruncateProps) => lines && ellipsedTextStyle(lines)}
`;

const TextClamp = ({
  children,
  lines = DEFAULT_CLAMPING_LINES,
  showTipOnHover = true,
  maxWidth = MAX_CONTENT_WIDTH,
}: Props) => {
  const textRef = useRef<HTMLSpanElement>();
  const [showTooltips, setShowTooltips] = useState(false);

  const handleOnTextOver = useCallback(() => {
    if (!showTipOnHover) {
      return setShowTooltips(false);
    }

    /* FF calculates scroll height using lines * lineHeight
    /* This causes that scrollHeight always is greater than clientHeight
    /* When text is clampled, scrollHeight is visible lines height + next line height
    */

    const clampHeight =
      textRef.current.clientHeight + textRef.current.clientHeight / lines;
    const heightOverflow = textRef.current.scrollHeight >= clampHeight;

    const widthOverflow =
      textRef.current.scrollWidth > textRef.current.offsetWidth;

    setShowTooltips(heightOverflow || widthOverflow);
  }, [lines, textRef, showTipOnHover]);

  return (
    <Tooltip>
      <Trigger>
        <Truncate lines={lines} ref={textRef} onMouseOver={handleOnTextOver}>
          {children}
        </Truncate>
      </Trigger>
      {showTooltips && (
        <Tooltip.Content position="bottom">
          <Box data-testid="content" maxWidth={maxWidth}>
            {children}
          </Box>
        </Tooltip.Content>
      )}
    </Tooltip>
  );
};

export { TextClamp };
